import { endOfToday } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import Loading from '~/components-ds/Loading';
import { config } from '~/config';
import { useAppDispatch, useBusinessDetails, useExtraInfo, usePayments, useQuote } from '~/hooks';
import { selectAppDetails } from '~/selectors/selectAppDetails';
import { GenerateTokenResponse, pciPalApi } from '~/services/pciPal';
import {
  updateBankAccountName,
  updateCreditCardCVV,
  updateCreditCardExpiryDate,
  updateCreditCardName,
  updateCreditCardNumberMasked,
  updateCreditCardType,
  updateCybersourceToken,
  updateIsCreditCardValid,
  updateTransactionDetail,
} from '~/slices/payments';
import { PaymentFrequencyOptions } from '~/types/payments';
import { getMonthlyPremium } from '~/utils';
import { toCreditCardType } from '~/utils/toCreditCardType';

import { parsePciPal } from './parsePciPal';

const tenantId = config.pciPalTenantId;

interface PciProps {
  onComplete: () => void;
}

export const Pci = ({ onComplete }: PciProps) => {
  const { premiums, quoteNumber } = useQuote();
  const { paymentFrequency } = usePayments();
  const { userEmail } = useSelector(selectAppDetails);
  const extraInfo = useExtraInfo();
  const businessDetails = useBusinessDetails();
  const dispatch = useAppDispatch();
  const formRef = useRef<HTMLFormElement>(null);
  const [session, setSession] = useState<string>();
  const [tokenDetail, setTokenDetail] = useState<GenerateTokenResponse>();
  /*
   * If the cover start date is in the future, we don't want to capture the payment.
   * Capturing will happen on the backend for future dated payments.
   */
  const isFutureDated =
    businessDetails.coverStart && new Date(JSON.parse(businessDetails.coverStart)) > endOfToday();
  const doCapture = paymentFrequency === PaymentFrequencyOptions.OneOff;

  const pciListener = (e: MessageEvent) => {
    if (e.origin !== config.pciPalUrl) return;
    const data = parsePciPal(e.data);
    if (
      (data.processorResponse === '00' ||
        data.processorResponse === '000' ||
        data.processorResponse === '85') &&
      data.decision === 'ACCEPT'
    ) {
      dispatch(updateCreditCardType(toCreditCardType(data.cardType)));
      const maskedNumber = data.maskedCardNumber;
      dispatch(updateCreditCardNumberMasked(maskedNumber));
      dispatch(updateCybersourceToken(data.cardToken));
      dispatch(updateCreditCardExpiryDate(`${data.expiryMonth}${data.expiryYear}`));
      dispatch(updateCreditCardCVV(data.creditCardCVV));
      dispatch(updateIsCreditCardValid(true));
      dispatch(updateBankAccountName(data.cardholderName));
      dispatch(updateCreditCardName(data.cardholderName));
      dispatch(
        updateTransactionDetail({
          transactionId: data.transactionId,
          reasonCode: data.reasonCode,
          authAmount: data.totalAmount,
          status: doCapture ? 'CAPTURED' : 'AUTHORIZED',
          cardType: data.cardType,
        })
      );
      onComplete();
    } else {
      console.log('Unhandled payment response', data);
    }
  };
  useEffect(() => {
    const doPayment = async () => {
      const tokenResponse = await dispatch(
        pciPalApi.endpoints.getToken.initiate(undefined)
      ).unwrap();
      setTokenDetail(tokenResponse);

      const totalPremium =
        premiums?.policy?.policyPremium?.transactionPremium?.totalPremium?.toFixed(2) ?? '0';

      const monthlyPremium =
        getMonthlyPremium(premiums?.policy?.payment?.instalments?.[0])?.toFixed(2) ?? '0';

      const amount =
        paymentFrequency === PaymentFrequencyOptions.OneOff ? totalPremium : monthlyPremium;

      const sessionResponse = await dispatch(
        pciPalApi.endpoints?.createSession.initiate({
          accessToken: tokenResponse.accessToken,
          amount: doCapture ? amount : '0.00',
          agentId: userEmail,
          firstName: extraInfo.firstName ?? undefined,
          lastName: extraInfo.lastName ?? undefined,
          primaryEmailAddress: extraInfo.email ?? undefined,
          mobilePhoneNumber: extraInfo.mobile ?? undefined,
          country: 'AU',
          currency: 'AUD',
          runCaptureService: doCapture ? 'true' : 'false',
          location: {
            addressLine1: businessDetails?.fullBusinessAddress?.streetAddress1,
            city: businessDetails?.fullBusinessAddress?.city,
            stateCode: businessDetails?.fullBusinessAddress?.stateCode,
            postalCode: businessDetails?.fullBusinessAddress?.postalCode,
          },
          merchantReferenceCode: quoteNumber ?? undefined,
        })
      ).unwrap();
      setSession(sessionResponse.sessionId);
    };
    window.addEventListener('message', pciListener, false);
    doPayment().catch((e) => {
      throw e;
    });
    return () => {
      window.removeEventListener('message', pciListener, false);
    };
  }, [
    businessDetails.coverStart,
    businessDetails?.fullBusinessAddress?.city,
    businessDetails?.fullBusinessAddress?.postalCode,
    businessDetails?.fullBusinessAddress?.stateCode,
    businessDetails?.fullBusinessAddress?.streetAddress1,
    dispatch,
    doCapture,
    extraInfo.email,
    extraInfo.firstName,
    extraInfo.lastName,
    extraInfo.mobile,
    isFutureDated,
    onComplete,
    paymentFrequency,
    premiums?.policy?.payment?.instalments,
    premiums?.policy?.policyPremium?.transactionPremium?.totalPremium,
    quoteNumber,
    userEmail,
  ]);

  useEffect(() => {
    if (!session) return;
    formRef.current?.submit();
  }, [session]);

  if (!session) return <Loading />;

  return (
    <>
      <form
        style={{ display: 'none' }}
        ref={formRef}
        target="pci"
        method="post"
        action={`${config.pciPalUrl}/session/${tenantId}/view/${session}/framed`}
      >
        <input type="hidden" name="x-bearer-token" value={tokenDetail?.accessToken} />
        <input type="hidden" name="x-refresh-token" value={tokenDetail?.refreshToken} />
        <input type="submit" />
      </form>
      {session && (
        <iframe
          title="pci"
          name="pci"
          style={{
            width: '100%',
            height: '70vh',
            border: 'none',
          }}
        />
      )}
    </>
  );
};
